import React, { useMemo } from 'react';
import { TeamInfo } from '../TeamInfo';
import { stintMinutes } from '../analytics/lapTime';
import { DEFAULT_RACE_RULES, RaceRules } from '../types';
import './PitWindow.css';

interface PitWindowProps {
  teams: Map<string, TeamInfo>;
  /** Per-event regulations; falls back to the defaults before they arrive. */
  rules?: RaceRules;
}

interface OnTrackKart {
  kartNumber: string;
  driver: string;
  stintMin: number;
  remaining: number;
}

export const PitWindow: React.FC<PitWindowProps> = ({
  teams,
  rules = DEFAULT_RACE_RULES,
}) => {
  const { maxStintMinutes, dueSoonMinutes } = rules;

  const { inPit, dueSoon, overLimit } = useMemo(() => {
    const inPit: TeamInfo[] = [];
    const onTrack: OnTrackKart[] = [];

    teams.forEach((team) => {
      if (team.inBox) {
        inPit.push(team);
        return;
      }
      const stintMin = stintMinutes(team.onTrackTime);
      if (stintMin == null) return;
      onTrack.push({
        kartNumber: team.kartNumber,
        driver: team.driver,
        stintMin,
        remaining: maxStintMinutes - stintMin,
      });
    });

    const dueSoon = onTrack
      .filter((k) => k.remaining <= dueSoonMinutes && k.remaining >= 0)
      .sort((a, b) => a.remaining - b.remaining);
    const overLimit = onTrack
      .filter((k) => k.remaining < 0)
      .sort((a, b) => a.remaining - b.remaining);

    return { inPit, dueSoon, overLimit };
  }, [teams, maxStintMinutes, dueSoonMinutes]);

  return (
    <div className="pit-window">
      <div className="pw-stats">
        <div className="pw-stat pw-stat--pit">
          <span className="pw-num">{inPit.length}</span>
          <span className="pw-lbl">In pit</span>
        </div>
        <div className="pw-stat pw-stat--soon">
          <span className="pw-num">{dueSoon.length}</span>
          <span className="pw-lbl">Due soon (≤{dueSoonMinutes}′)</span>
        </div>
        <div className="pw-stat pw-stat--over">
          <span className="pw-num">{overLimit.length}</span>
          <span className="pw-lbl">Over {maxStintMinutes}′</span>
        </div>
      </div>

      {(dueSoon.length > 0 || overLimit.length > 0) && (
        <div className="pw-list">
          {overLimit.map((k) => (
            <span key={`o-${k.kartNumber}`} className="pw-chip pw-chip--over" title={k.driver}>
              #{k.kartNumber} <b>+{Math.abs(k.remaining)}′ over</b>
            </span>
          ))}
          {dueSoon.map((k) => (
            <span key={`s-${k.kartNumber}`} className="pw-chip pw-chip--soon" title={k.driver}>
              #{k.kartNumber} <b>{k.remaining}′ left</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default PitWindow;
